/**
 * Phase 39 — pure, DB-free helpers for the member-profile "Charges due" rail
 * (the member_charges/non-Plan collection surface, distinct from the Plan
 * rail's `pendingDues`/`MemberCollectPayment`).
 *
 * Collectible predicate (SC5, the crux of this phase): a `ContactTransactionRow`
 * is collectible ONLY when it carries a linked `memberChargeId` AND that
 * charge's status is exactly "Pending". This intentionally EXCLUDES
 * Billed/Disputed/Paid/Waived — do NOT widen this to account-detail.tsx's
 * looser `status !== "Waived"` check (a latent quirk documented in
 * 39-RESEARCH.md Pitfall 2: the monthly billing-scheduler sweeps Pending
 * non-Plan charges into Billed, and PATCHing a Billed row 422s).
 *
 * Plan 07 (gap closure, 39-UAT.md) — SECOND, DISJOINT rail: production F&B
 * stock-outs are frequently written with `charging_decision: NULL`, so no
 * `member_charges` row is ever created (`memberChargeId` stays null forever).
 * The real payment state for those rows lives on `stock_out.payment_status`,
 * surfaced as `ContactTransactionRow.status` by 39-06's server-side status
 * derivation. `isStockOutRowCollectible` recognises THAT shape — a charge-less,
 * unpaid stock_out row — as collectible via a separate settlement path (PATCH
 * the stock-out directly, not `updateMemberCharge`). Because it requires
 * `memberChargeId == null`, it is mutually exclusive with `isChargeRowCollectible`
 * (which requires `memberChargeId != null`) by construction — a row can never
 * be collectible via both rails, so it can never be double-collected.
 *
 * No JSX, no hooks, no DB access — safe to unit-test directly (see
 * artifacts/crm/src/__tests__/member-charge-collection-phase39.test.ts and
 * artifacts/crm/src/__tests__/member-charge-collection-phase39-gap.test.ts).
 */
import type { ContactTransactions, ContactTransactionRow } from "@workspace/api-client-react";

/** Every bucket EXCEPT `plan` — Plan rows never carry a `memberChargeId`
 *  (they're membership_payment_periods, not member_charges) and are never
 *  collectible from this rail (that's the Plan rail's job).
 *
 * Phase 40 (40-07) — `"accountLevel"` added: the FIX-2 billing-contact
 * rollup bucket (still-unattributed account-level Pending non-Plan
 * `member_charges`, `source: "member_charge"`). These rows already carry a
 * `memberChargeId` (their own id — see 40-RESEARCH.md "FIX 2 needs NO third
 * rail"), so `isChargeRowCollectible` picks them up for free; no predicate
 * change was needed, only including the bucket in the iteration below. */
const NON_PLAN_BUCKETS: (keyof ContactTransactions)[] = [
  "booking",
  "conferenceRoom",
  "fnb",
  "prints",
  "stationery",
  "addOns",
  "accountLevel",
];

/**
 * True ONLY when the row has a linked member_charges id AND that charge is
 * still Pending. False for Billed/Disputed/Paid/Waived, and false whenever
 * `memberChargeId` is null/undefined (e.g. a Free/Comp stock-out that never
 * created a member_charges row) regardless of status.
 */
export function isChargeRowCollectible(
  row: Pick<ContactTransactionRow, "memberChargeId" | "status">,
): boolean {
  return row.memberChargeId != null && row.status === "Pending";
}

/**
 * Set of every collectible non-Plan `memberChargeId` across the account's
 * transactions — the grouped-collect selection set (SC2). Empty Set when
 * `txns` hasn't been fetched yet.
 */
export function collectibleChargeIds(txns: ContactTransactions | undefined): Set<number> {
  const ids = new Set<number>();
  if (!txns) return ids;
  for (const bucket of NON_PLAN_BUCKETS) {
    for (const row of txns[bucket] ?? []) {
      if (isChargeRowCollectible(row) && row.memberChargeId != null) {
        ids.add(row.memberChargeId);
      }
    }
  }
  return ids;
}

/**
 * SECOND, DISJOINT rail (Plan 07 gap closure — 39-UAT.md GAP-39-A2). True
 * ONLY for a charge-less, unpaid stock_out row: `source === "stock_out"`,
 * `memberChargeId == null` (nothing to settle via `updateMemberCharge` —
 * settle by PATCHing the stock-out itself), `status === "Pending"` (as
 * derived from `stock_out.payment_status` by 39-06 — NULL/'' payment_status
 * renders as "Pending" upstream), and a positive `amount`.
 *
 * `memberChargeId == null` here vs. `memberChargeId != null` in
 * `isChargeRowCollectible` is what makes the two predicates mutually
 * exclusive for EVERY row, by construction — see the single-rail
 * cross-product test in member-charge-collection-phase39-gap.test.ts.
 */
export function isStockOutRowCollectible(
  row: Pick<ContactTransactionRow, "source" | "memberChargeId" | "status" | "amount">,
): boolean {
  return (
    row.source === "stock_out" &&
    row.memberChargeId == null &&
    row.status === "Pending" &&
    row.amount != null &&
    row.amount > 0
  );
}

/**
 * Union of both rails — used by the per-row action gate (39-08) and by
 * `sumChargesDue` below. True when a row is collectible via EITHER the
 * member_charges rail OR the stock_out rail (never both — see
 * `isStockOutRowCollectible`'s doc comment).
 */
export function isRowCollectible(
  row: Pick<ContactTransactionRow, "source" | "memberChargeId" | "status" | "amount">,
): boolean {
  return isChargeRowCollectible(row) || isStockOutRowCollectible(row);
}

/**
 * Disjoint split of the account's collectible non-Plan rows into the two
 * settlement rails: `chargeIds` (memberChargeId — settle via
 * `updateMemberCharge`) and `stockOutIds` (the stock_out row's own `id` —
 * settle by PATCHing the stock-out). By construction (the two predicates are
 * mutually exclusive) no id can ever appear in both arrays, so no underlying
 * sale is ever exposed as a collect target on more than one rail. Empty
 * arrays when `txns` hasn't been fetched yet.
 */
export function collectTargets(
  txns: ContactTransactions | undefined,
): { chargeIds: number[]; stockOutIds: number[] } {
  const chargeIds: number[] = [];
  const stockOutIds: number[] = [];
  if (!txns) return { chargeIds, stockOutIds };
  for (const bucket of NON_PLAN_BUCKETS) {
    for (const row of txns[bucket] ?? []) {
      if (isChargeRowCollectible(row) && row.memberChargeId != null) {
        chargeIds.push(row.memberChargeId);
      } else if (isStockOutRowCollectible(row)) {
        stockOutIds.push(row.id);
      }
    }
  }
  return { chargeIds, stockOutIds };
}

/**
 * Sum of `amount` over EVERY collectible non-Plan row across BOTH rails —
 * the "Charges due ₹X" total shown on the member profile. Client-derived
 * from the already-fetched `useGetContactTransactions` data (no new
 * ContactStats field/endpoint — see 39-RESEARCH.md Pattern 4). Broadened in
 * Plan 07 (was member_charges-rail only) so the total reflects real,
 * charge-less F&B consumption too (39-UAT.md). 0 when `txns` is undefined.
 */
export function sumChargesDue(txns: ContactTransactions | undefined): number {
  if (!txns) return 0;
  let total = 0;
  for (const bucket of NON_PLAN_BUCKETS) {
    for (const row of txns[bucket] ?? []) {
      if (isRowCollectible(row)) {
        total += row.amount ?? 0;
      }
    }
  }
  return total;
}
